import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Languages } from 'lucide-react';
import ShowcaseStage from './ShowcaseStage.jsx';
import {
  LANGUAGE_MORPH,
  clampFrame,
  getLanguageSegment,
  orderTranslationTargets
} from './showcaseTimeline.js';
import { useInView, useReducedMotion, useShowcaseRuntime } from './useShowcaseRuntime.js';

// Autonyms are language-invariant, so they live here rather than in the copy.
const TARGET_NAMES = {
  tr: 'Türkçe',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  pt: 'Português',
  ru: 'Русский',
  ja: '日本語',
  zh: '中文',
  ar: 'العربية'
};

// Same English sample as TRANSLATION_SAMPLE in remotion/data.js. Phones show
// only the translated card, so the original is repeated here as page text.
const ORIGINAL_SAMPLE_TITLE = 'Hiker overlooking a calm alpine lake at sunrise';

const DURATION = LANGUAGE_MORPH.durationInFrames;
const LAST_FRAME = DURATION - 1;
// A picked language holds until the visitor scrolls this far away.
const MANUAL_RELEASE_PX = 140;

const segmentIndexFor = (frame, count) => Math.min(
  count - 1,
  Math.max(0, Math.floor((frame - LANGUAGE_MORPH.intro) / LANGUAGE_MORPH.segment))
);

const HomeShowcaseLanguages = ({ copy, language = 'en', localeTag = 'en-US', theme = 'true-dark' }) => {
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const playerRef = useRef(null);
  const frameRef = useRef(0);
  const lastSeekRef = useRef(-1);
  const activeRef = useRef(0);
  const manualRef = useRef(null);

  const chipsRef = useRef(null);
  const runtime = useShowcaseRuntime(rootRef, { language });
  const reducedMotion = useReducedMotion();
  const nearViewport = useInView(rootRef, { threshold: 0, rootMargin: '200px 0px' });
  const order = useMemo(() => orderTranslationTargets(language), [language]);
  const [active, setActive] = useState(0);
  const [live, setLive] = useState(false);

  const inputProps = useMemo(
    () => ({ copy, language, localeTag, theme, order }),
    [copy, language, localeTag, order, theme]
  );

  const seek = useCallback((frame) => {
    const next = clampFrame(frame, DURATION);
    if (next === lastSeekRef.current) return;
    lastSeekRef.current = next;
    playerRef.current?.seekTo(next);
  }, []);

  const handleFrame = useCallback((frame) => {
    frameRef.current = frame;
    lastSeekRef.current = frame;
    const index = segmentIndexFor(frame, order.length);
    if (index !== activeRef.current) {
      activeRef.current = index;
      setActive(index);
    }
  }, [order.length]);

  const handleLive = useCallback(() => setLive(true), []);

  // The translation follows the stage's trip through the viewport: entering
  // at the bottom starts the first language, leaving the top ends the last.
  useEffect(() => {
    if (reducedMotion || !live || !nearViewport) return undefined;
    let rafId = 0;
    let running = false;
    let current = frameRef.current;

    const targetFrame = () => {
      const node = stageRef.current;
      if (!node) return current;
      const rect = node.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      const start = viewport * 0.92;
      const end = viewport * 0.12 - rect.height * 0.35;
      const progress = Math.min(1, Math.max(0, (start - rect.top) / (start - end)));
      return progress * LAST_FRAME;
    };

    const tick = () => {
      const manual = manualRef.current;
      if (manual) {
        if (Math.abs(window.scrollY - manual.scrollY) < MANUAL_RELEASE_PX) {
          running = false;
          return;
        }
        manualRef.current = null;
        playerRef.current?.pause();
        current = frameRef.current;
      }
      const target = targetFrame();
      const diff = target - current;
      current = Math.abs(diff) < 0.4 ? target : current + diff * 0.18;
      seek(current);
      if (Math.abs(target - current) >= 0.4) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        running = false;
      }
    };

    const kick = () => {
      if (running) return;
      running = true;
      rafId = window.requestAnimationFrame(tick);
    };

    if (!manualRef.current) {
      current = targetFrame();
      seek(current);
    }
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', kick);
      window.removeEventListener('resize', kick);
    };
  }, [live, nearViewport, reducedMotion, seek]);

  // On phones the chip row scrolls sideways; keep the active language in it
  // without moving the page.
  useEffect(() => {
    const row = chipsRef.current;
    const chip = row?.children[active];
    if (!row || !chip || row.scrollWidth <= row.clientWidth + 1) return;
    const left = chip.offsetLeft - row.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2;
    row.scrollTo({ left: Math.max(0, left), behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [active, reducedMotion]);

  // Reduced motion: show the finished state of the selected language.
  useEffect(() => {
    if (!reducedMotion || !live) return;
    playerRef.current?.pause();
    seek(getLanguageSegment(activeRef.current).hold);
  }, [live, reducedMotion, seek]);

  const pickLanguage = (index) => {
    const segment = getLanguageSegment(index);
    activeRef.current = index;
    setActive(index);
    const player = playerRef.current;
    if (!player) return;
    if (reducedMotion) {
      seek(segment.hold);
      return;
    }
    // Replay the typing for the picked language, then hold until the visitor
    // scrolls on. Each pick owns the stage until a newer pick or a scroll.
    const pick = { scrollY: window.scrollY };
    manualRef.current = pick;
    lastSeekRef.current = -1;
    seek(segment.from);
    player.play();
    const stopAt = segment.hold;
    const watch = () => {
      if (manualRef.current !== pick) return;
      if (frameRef.current >= stopAt || !player.isPlaying()) {
        player.pause();
        seek(stopAt);
        return;
      }
      window.requestAnimationFrame(watch);
    };
    window.requestAnimationFrame(watch);
  };

  if (!copy) return null;

  return (
    <div
      ref={rootRef}
      id="guest-showcase-translate"
      className={`stk-showcase stk-languages${live ? ' is-live' : ''}`}
      lang={language}
    >
      <div className="stk-languages-head" data-home-reveal>
        <p className="premium-eyebrow">
          <Languages size={14} aria-hidden="true" />
          {copy.eyebrow}
        </p>
        <h3 className="stk-subheading">{copy.title}</h3>
        <p className="stk-languages-description">{copy.description}</p>
      </div>

      <p className="stk-languages-original">
        <span className="stk-language-code" aria-hidden="true">EN</span>
        <span>
          <strong>{copy.scene?.originalLabel}</strong>
          <span lang="en">{ORIGINAL_SAMPLE_TITLE}</span>
        </span>
      </p>

      <div className="stk-languages-stage" ref={stageRef}>
        <ShowcaseStage
          ref={playerRef}
          runtime={runtime}
          compositionId="languages"
          inputProps={inputProps}
          onFrame={handleFrame}
          onLive={handleLive}
          variant="wide"
        />
      </div>

      <div className="stk-language-chips" ref={chipsRef} role="group" aria-label={copy.chipsLabel}>
        {order.map((code, index) => (
          // Named by the lang-tagged autonym itself, so screen readers
          // pronounce 日本語 or العربية in their own language.
          <button
            key={code}
            type="button"
            className={`stk-language-chip${index === active ? ' is-active' : ''}`}
            aria-pressed={index === active}
            disabled={!live}
            onClick={() => pickLanguage(index)}
          >
            <span className="stk-language-code" aria-hidden="true">{code.toUpperCase()}</span>
            <span lang={code} dir={code === 'ar' ? 'rtl' : undefined}>{TARGET_NAMES[code]}</span>
          </button>
        ))}
      </div>
      <p className="stk-languages-note">{copy.sampleNote}</p>
    </div>
  );
};

export default HomeShowcaseLanguages;
